import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Empty, Spin } from 'antd';
import {
  BulbOutlined,
  CodeSandboxOutlined,
  FileAddOutlined,
  FolderOutlined,
  GoldOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { paths } from '@app/routes/paths';
import { useSearch } from '@presentation/hooks/useSearch';
import { useCollections } from '@presentation/hooks/useCollections';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';
import { useThemeStore } from '@presentation/stores/themeStore';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import { MethodBadge } from '@presentation/components/workbench/MethodBadge';
import type { HttpMethod } from '@core/entities/ApiRequest';

interface CommandPaletteProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onOpenCookies: () => void;
  onOpenHistory: () => void;
  onOpenConsole: () => void;
}

interface PaletteItem {
  key: string;
  section: 'Commands' | 'Collections' | 'Folders' | 'Requests';
  icon: React.ReactNode;
  label: string;
  hint?: string;
  run: () => void;
}

export const CommandPalette = ({
  workspaceId,
  open,
  onClose,
  onOpenCookies,
  onOpenHistory,
  onOpenConsole,
}: CommandPaletteProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);

  const openNewRequestTab = useWorkbenchStore((state) => state.openNewRequestTab);
  const openRequestTab = useWorkbenchStore((state) => state.openRequestTab);
  const reopenLastClosedTab = useWorkbenchStore((state) => state.reopenLastClosedTab);
  const closedTabCount = useWorkbenchStore((state) => state.closedTabs.length);
  const themeMode = useThemeStore((state) => state.mode);
  const toggleTheme = useThemeStore((state) => state.toggle);
  const { data: collections } = useCollections(workspaceId);
  const { data: results, isFetching } = useSearch(workspaceId, debouncedQuery);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  // Reset the palette's state when it transitions to open, adjusted during
  // render (not in an effect) to avoid a cascading-render lint violation —
  // the focus() call is the only true external-system side effect, so it's
  // the only part left in a useEffect below.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setActiveIndex(0);
    }
  }

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const openRequestById = useCallback(
    (id: string) => {
      void queryClient
        .fetchQuery({
          queryKey: QUERY_KEYS.apiRequest(id),
          queryFn: () => container.apiRequests.get(id),
        })
        .then((request) => openRequestTab(workspaceId, request));
    },
    [queryClient, openRequestTab, workspaceId],
  );

  const commands: PaletteItem[] = useMemo(() => {
    const firstCollectionId = collections?.[0]?.id;
    const items: PaletteItem[] = [
      {
        key: 'cmd-new-request',
        section: 'Commands',
        icon: <FileAddOutlined />,
        label: 'New request',
        run: () => firstCollectionId && openNewRequestTab(workspaceId, firstCollectionId, null),
      },
      ...(closedTabCount > 0
        ? [
            {
              key: 'cmd-reopen-closed',
              section: 'Commands' as const,
              icon: <ReloadOutlined />,
              label: 'Reopen last closed tab',
              run: reopenLastClosedTab,
            },
          ]
        : []),
      {
        key: 'cmd-cookies',
        section: 'Commands',
        icon: <GoldOutlined />,
        label: 'Open Cookie Jar',
        run: onOpenCookies,
      },
      {
        key: 'cmd-history',
        section: 'Commands',
        icon: <HistoryOutlined />,
        label: 'Open History',
        run: onOpenHistory,
      },
      {
        key: 'cmd-console',
        section: 'Commands',
        icon: <CodeSandboxOutlined />,
        label: 'Open Console',
        run: onOpenConsole,
      },
      {
        key: 'cmd-theme',
        section: 'Commands',
        icon: <BulbOutlined />,
        label: themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        run: toggleTheme,
      },
      {
        key: 'cmd-settings',
        section: 'Commands',
        icon: <SettingOutlined />,
        label: 'Go to Settings',
        run: () => navigate(paths.settings),
      },
      {
        key: 'cmd-workspaces',
        section: 'Commands',
        icon: <SettingOutlined />,
        label: 'Go to Workspaces',
        run: () => navigate(paths.workspaces),
      },
    ];

    const q = query.trim().toLowerCase();
    return q === '' ? items : items.filter((item) => item.label.toLowerCase().includes(q));
  }, [
    collections,
    workspaceId,
    themeMode,
    query,
    navigate,
    onOpenCookies,
    onOpenHistory,
    onOpenConsole,
    openNewRequestTab,
    toggleTheme,
    closedTabCount,
    reopenLastClosedTab,
  ]);

  const searchItems: PaletteItem[] = useMemo(() => {
    if (!results) return [];

    const collectionItems: PaletteItem[] = results.collections.map((c) => ({
      key: `collection-${c.id}`,
      section: 'Collections',
      icon: <FolderOutlined />,
      label: c.name,
      hint: c.description ?? undefined,
      run: () => navigate(paths.workbench(workspaceId)),
    }));

    const folderItems: PaletteItem[] = results.folders.map((f) => ({
      key: `folder-${f.id}`,
      section: 'Folders',
      icon: <FolderOutlined />,
      label: f.name,
      run: () => navigate(paths.workbench(workspaceId)),
    }));

    const requestItems: PaletteItem[] = results.requests.map((r) => ({
      key: `request-${r.id}`,
      section: 'Requests',
      icon: <MethodBadge method={r.method as HttpMethod} className="w-10 shrink-0 text-left" />,
      label: r.name,
      run: () => openRequestById(r.id),
    }));

    return [...requestItems, ...collectionItems, ...folderItems];
  }, [results, navigate, workspaceId, openRequestById]);

  const allItems = debouncedQuery.trim() === '' ? commands : [...commands, ...searchItems];

  const execute = (item: PaletteItem) => {
    item.run();
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, allItems.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = allItems[activeIndex];
      if (item) execute(item);
    }
  };

  let lastSection: string | null = null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      style={{ top: 96 }}
      styles={{ body: { padding: 0 } }}
      destroyOnHidden
    >
      <div className="flex items-center gap-2 border-b border-black/10 px-3 py-2.5 dark:border-white/10">
        <SearchOutlined className="text-gray-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search requests, collections, or run a command…"
          className="min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
          aria-label="Command palette"
          role="combobox"
          aria-expanded
          aria-controls="command-palette-list"
        />
        {isFetching && <Spin size="small" />}
      </div>

      <div id="command-palette-list" role="listbox" className="max-h-96 overflow-y-auto p-1.5">
        {allItems.length === 0 && (
          <Empty className="py-8" description="No matches" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}

        {allItems.map((item, index) => {
          const showHeader = item.section !== lastSection;
          lastSection = item.section;

          return (
            <div key={item.key}>
              {showHeader && (
                <div className="px-2 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  {item.section}
                </div>
              )}
              <div
                role="option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => execute(item)}
                className={`flex cursor-pointer items-center gap-2.5 rounded px-2.5 py-1.5 text-sm ${
                  index === activeIndex ? 'bg-black/5 dark:bg-white/10' : ''
                }`}
              >
                <span className="shrink-0">{item.icon}</span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.hint && (
                  <span className="shrink-0 truncate text-xs text-gray-400">{item.hint}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
